import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { DIMENSIONS, rung, signed } from "../lib/bopta";

const BIG = 15;

const READS = {
  me: [(n) => `You run ${n} more independently than you let your team run it.`, (n) => `You see your team running ${n} more independently than you run it yourself.`, "Your two views"],
  admin: [(n) => `They run ${n} more independently than they let their team run it.`, (n) => `They see their team running ${n} more independently than they run it themselves.`, "Their two views"],
};

export const gapRead = (staff, manager, who = "me") => {
  const [up, down, close] = READS[who];
  const gaps = DIMENSIONS.map((d) => ({ d, g: staff.dims[d.k] - manager.dims[d.k] }));
  const top = gaps.reduce((p, c) => (Math.abs(c.g) > Math.abs(p.g) ? c : p));
  const n = top.d.name;
  if (top.g >= BIG) return up(n);
  if (top.g <= -BIG) return down(n);
  return `${close} line up closely. The biggest difference is ${n}, at ${Math.abs(Math.round(top.g))} points.`;
};

const Bar = ({ v, color, faded }) => (
  <div className="h-2 rounded-full bg-[#EFEFEA]"><div className="h-2 rounded-full" style={{ width: `${v}%`, background: color, opacity: faded ? 0.45 : 1 }} /></div>
);

const DimRow = ({ d, s, m }) => {
  const g = s - m;
  return (
    <div data-testid={`compare-dim-${d.k}`}>
      <div className="mb-1 flex items-center justify-between text-sm">
        <span className="flex items-center gap-2 font-medium"><span className="chip" style={{ background: d.color }}>{d.k}</span>{d.name}
          {Math.abs(g) >= BIG && <span className="rounded-full bg-[#FDEFE6] px-2 py-0.5 text-[10px] font-bold uppercase text-[var(--orange)]" data-testid={`compare-big-gap-${d.k}`}>Big gap</span>}
        </span>
        <span className="text-xs muted"><b className="text-[var(--ink)]">{Math.round(s)}</b> vs <b className="text-[var(--ink)]">{Math.round(m)}</b> · <span data-testid={`compare-gap-${d.k}`}>{signed(g)}</span></span>
      </div>
      <div className="space-y-1"><Bar v={s} color={d.color} /><Bar v={m} color={d.color} faded /></div>
    </div>
  );
};

const Side = ({ label, r, testid }) => (
  <div className="rounded-xl bg-[#F4F4F0] p-4" data-testid={testid}>
    <p className="text-xs muted">{label}</p>
    <p className="display mt-1 text-3xl font-extrabold">{Math.round(r.overall)}</p>
    <p className="text-sm">L{rung(r.overall).n} {rung(r.overall).translation}</p>
  </div>
);

export const ComparePanel = ({ current, other, admin = false }) => {
  const staff = current.mode === "staff" ? current : other;
  const manager = current.mode === "staff" ? other : current;
  return (
    <section className={admin ? "" : "panel mt-5 p-7"} data-testid="compare-panel">
      <p className="kicker">{admin ? "Their two views" : "Your two views"}</p>
      <h2 className="mt-2 text-2xl font-bold">{admin ? "Own autonomy vs how independent they think their team is" : "My own autonomy vs how independent I think my team is"}</h2>
      <p className="mt-3 font-medium text-[var(--teal)]" data-testid="compare-read">{gapRead(staff, manager, admin ? "admin" : "me")}</p>
      <div className="mt-6 grid gap-6 lg:grid-cols-[260px_1fr]">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-1">
          <Side label={`${admin ? "Own" : "My own"} autonomy (staff lens)`} r={staff} testid="compare-staff-overall" />
          <Side label={`${admin ? "Their" : "My"} team's independence (manager lens)`} r={manager} testid="compare-manager-overall" />
        </div>
        <div className="space-y-4">
          <p className="text-xs muted">Top bar: {admin ? "them" : "you"}. Lighter bar: {admin ? "their" : "your"} team. Gap = {admin ? "them" : "you"} minus team.</p>
          {DIMENSIONS.map((d) => <DimRow key={d.k} d={d} s={staff.dims[d.k]} m={manager.dims[d.k]} />)}
        </div>
      </div>
      {!admin && <Link to={`/results/${other.id}`} state={{ compareWith: current.id }} className="btn btn-line mt-6 no-print" data-testid="compare-open-other">
        Open my {other.mode === "manager" ? "manager" : "staff"} result <ArrowRight className="h-4 w-4" />
      </Link>}
    </section>
  );
};
